import { redirectAfterLogin } from '../shared/auth.js';
import { __ as translate, resolveLocale, getLocale } from '../shared/i18n.js';
import { baseLanguage } from '../shared/i18nBase.js';
import { ensureSimpleWebAuthn } from '../shared/cdnScripts.js';

function acceptLanguageForLogin() {
    return baseLanguage( getLocale() || resolveLocale() );
}

export function loginPage() {
    return {
        loginForm: {
            email: '',
            password: ''
        },
        loginPasswordVisible: false,
        loginError: '',
        isLoading: false,

        init() {
            const params = new URLSearchParams( location.search );
            if ( params.get( 'expired' ) === '1' ) {
                this.loginError = translate( 'Your session has expired. Please log in again.' );
                history.replaceState( null, '', '/login' );
            } else if ( params.get( 'no_bots' ) === '1' ) {
                this.loginError = translate( 'No bots are available for your account yet.' );
                history.replaceState( null, '', '/login' );
            }
        },

        async login() {
            this.isLoading = true;
            this.loginError = '';

            try {
                const response = await fetch( '/api/auth/login', {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': acceptLanguageForLogin()
                    },
                    body: JSON.stringify( {
                        email: this.loginForm.email,
                        password: this.loginForm.password
                    } )
                } );

                const body = await response.json();

                if ( response.ok && body.data ) {
                    await redirectAfterLogin( body.data );
                    return;
                }
                this.loginError = body.message || body.error || translate( 'Login failed' );
                this.isLoading = false;
            } catch ( error ) {
                this.loginError = translate( 'Network error. Please try again.' );
                console.error( 'Login error:', error );
                this.isLoading = false;
            }
        },

        async loginWithPasskey() {
            this.isLoading = true;
            this.loginError = '';

            try {
                const optionsResp = await fetch( '/api/auth/webauthn/login/options', {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': acceptLanguageForLogin()
                    },
                    body: JSON.stringify( {
                        email: this.loginForm.email.trim() || undefined
                    } )
                } );
                const optionsBody = await optionsResp.json();
                if ( !optionsResp.ok || !optionsBody.data ) {
                    this.loginError = optionsBody.message || translate( 'Passkey login failed' );
                    this.isLoading = false;
                    return;
                }

                await ensureSimpleWebAuthn();
                const authResp = await SimpleWebAuthnBrowser.startAuthentication( {
                    optionsJSON: optionsBody.data.options
                } );

                const verifyResp = await fetch( '/api/auth/webauthn/login/verify', {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': acceptLanguageForLogin()
                    },
                    body: JSON.stringify( {
                        ...authResp,
                        challengeId: optionsBody.data.challengeId
                    } )
                } );
                const verifyBody = await verifyResp.json();

                if ( verifyResp.ok && verifyBody.data ) {
                    await redirectAfterLogin( verifyBody.data );
                    return;
                }
                this.loginError = verifyBody.message || verifyBody.error || translate( 'Passkey login failed' );
                this.isLoading = false;
            } catch ( error ) {
                if ( error.name === 'NotAllowedError' ) {
                    this.loginError = translate( 'Passkey login was cancelled.' );
                } else {
                    this.loginError = translate( 'Passkey login failed. Please try again.' );
                }
                console.error( 'Passkey login error:', error );
                this.isLoading = false;
            }
        }
    };
}
